import { useState, useEffect } from 'react';
import { useSignalR } from '../context/SignalRContext';
import type {
  PrestigeStatus,
  PrestigeUpgrade,
  PrestigeResult
} from '../services/prestigeService';
import {
  getPrestigeStatus,
  getPrestigeUpgrades,
  performPrestige
} from '../services/prestigeService';
import type { GameStateDto } from '../types/gameState';
import type { HubConnection } from '@microsoft/signalr';

export function usePrestige(playerId: string, gameState: GameStateDto | null, refreshGameState: () => Promise<void>) {
  const { connection } = useSignalR();
  const [prestigeStatus, setPrestigeStatus] = useState<PrestigeStatus | null>(null);
  const [availableUpgrades, setAvailableUpgrades] = useState<PrestigeUpgrade[]>([]);
  const [selectedUpgrade, setSelectedUpgrade] = useState<PrestigeUpgrade | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPrestigeModal, setShowPrestigeModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [prestigeResult, setPrestigeResult] = useState<PrestigeResult | null>(null);

  // Function to load prestige status
  const loadPrestigeStatus = async () => {
    if (!playerId) {
      console.warn('DEBUG: No playerId provided to loadPrestigeStatus');
      return;
    }

    // Use gameState.prestigeStatus if available
    if (gameState && gameState.prestigeStatus) {
      setPrestigeStatus(gameState.prestigeStatus);

      // Only show the prestige modal if the player can prestige and it's not already shown
      if (gameState.prestigeStatus.canPrestige && !showPrestigeModal && !showResultModal) {
        // If we have mothership data, load the upgrades
        if (gameState.mothership?.upgradeBranches) {
          console.log('Loading prestige upgrades from mothership data');
          loadPrestigeUpgrades();
        } else {
          console.log('No mothership data available, using API for prestige upgrades');
          loadPrestigeUpgrades();
        }
      }
      return;
    }

    // Fall back to the API if prestigeStatus is not in gameState
    try {
      if (!connection) return;
      setLoading(true);
      const status = await getPrestigeStatus(connection, playerId);
      console.log('Prestige status response from API:', status);
      setPrestigeStatus(status);

      // Only show the prestige modal if the player can prestige and it's not already shown
      if (status.canPrestige && !showPrestigeModal && !showResultModal) {
        loadPrestigeUpgrades();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load prestige status');
      console.error('Error loading prestige status:', err);
    } finally {
      setLoading(false);
    }
  };

  // Function to load available prestige upgrades
  const loadPrestigeUpgrades = async (forceAPI = false) => {
    if (!playerId) return;

    // Use mothership data from gameState if available and not forcing API
    if (!forceAPI && gameState && gameState.mothership?.upgradeBranches) {
      console.log('ALL mothership upgrades:', gameState.mothership.upgradeBranches);

      // Extract available upgrades from all mothership upgrade branches
      const availableUpgradesFromMothership: PrestigeUpgrade[] = [];

      // Process each branch separately
      gameState.mothership.upgradeBranches.forEach(branch => {
        // Find all unlocked upgrades in this branch
        const unlockedUpgrades = branch.upgrades.filter(upgrade => upgrade.isUnlocked);

        // If all upgrades in this branch are unlocked, include the highest level one as a "max level" indicator
        if (unlockedUpgrades.length === branch.upgrades.length && unlockedUpgrades.length > 0) {
          // Get the highest level upgrade
          const maxUpgrade = [...branch.upgrades].sort((a, b) => b.level - a.level)[0];
          availableUpgradesFromMothership.push({
            id: maxUpgrade.id,
            name: maxUpgrade.name,
            description: maxUpgrade.description || '',
            branchType: branch.branchType,
            level: maxUpgrade.level,
            bonusValue: maxUpgrade.bonusValue,
            isMaxLevel: true // Flag to indicate this is a max level upgrade
          });
        }
        // Otherwise, return the first not-yet-unlocked upgrade in this branch
        else {
          const nextUpgrade = branch.upgrades.find(upgrade => !upgrade.isUnlocked);
          if (nextUpgrade) {
            availableUpgradesFromMothership.push({
              id: nextUpgrade.id,
              name: nextUpgrade.name,
              description: nextUpgrade.description || '',
              branchType: branch.branchType,
              level: nextUpgrade.level,
              bonusValue: nextUpgrade.bonusValue
            });
          }
        }
      });

      console.log('Final available upgrades from mothership data:', availableUpgradesFromMothership);
      setAvailableUpgrades(availableUpgradesFromMothership);

      // Don't auto-select any upgrade initially - let the user choose
      setSelectedUpgrade(null);

      // If we're not forcing API, we can return here
      if (!forceAPI) return;

      // Otherwise, continue to API fetch below
      console.log('ForceAPI=true, fetching from API despite having mothership data');
    }

    // Fetch from API if mothership data isn't available or if forceAPI is true
    try {
      if (!connection) return;
      setLoading(true);
      console.log('Fetching prestige upgrades from API for player:', playerId);
      const options = await getPrestigeUpgrades(connection, playerId);
      console.log('API prestige upgrades response:', options);

      if (options.availableUpgrades.length === 0) {
        console.warn('API returned no prestige upgrades!');
      }

      setAvailableUpgrades(options.availableUpgrades);

      // Don't auto-select any upgrade initially - let the user choose
      setSelectedUpgrade(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load prestige upgrades');
      console.error('Error loading prestige upgrades:', err);
    } finally {
      setLoading(false);
    }
  };

  // Function to perform prestige
  const doPrestige = async () => {
    if (!playerId || !selectedUpgrade) return;

    try {
      if (!connection) return;
      setLoading(true);
      const result = await performPrestige(connection, playerId, selectedUpgrade.id);
      setPrestigeResult(result);

      // Close the prestige modal and show the result modal
      setShowPrestigeModal(false);
      setShowResultModal(true);

      // Clear upgrade selection
      setSelectedUpgrade(null);

      // Clear available upgrades cache to force a refresh
      setAvailableUpgrades([]);

      // Refresh the game state to reflect changes
      await refreshGameState();

      // Force reload prestige upgrades from API after a short delay to ensure state is updated
      // Pass true to forceAPI to bypass cache and always fetch from API
      setTimeout(() => {
        console.log("Forcing API reload of prestige upgrades after prestige");
        loadPrestigeStatus(); // First reload the status
        loadPrestigeUpgrades(true); // Then force reload the upgrades from API
      }, 750);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to perform prestige');
      console.error('Error performing prestige:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load prestige status when component mounts or game state changes
  useEffect(() => {
    loadPrestigeStatus();
  }, [playerId, gameState?.prestigeStatus, gameState?.mothership?.upgradeBranches, gameState?.playerStats?.currentPrestigeScore]);

  return {
    prestigeStatus,
    availableUpgrades,
    selectedUpgrade,
    setSelectedUpgrade,
    loading,
    error,
    showPrestigeModal,
    setShowPrestigeModal,
    showResultModal,
    setShowResultModal,
    prestigeResult,
    loadPrestigeStatus,
    loadPrestigeUpgrades,
    doPrestige
  };
}
